/**
 * A workflow's three text fields, laid out the same on the New page and the
 * workflow's own page: the name, a short description twice its width (both
 * shown in the list, one line each), and beneath them a long description for
 * whoever needs to know in detail what the workflow does, shown only here.
 */

import { CharCount } from '../../shared/CharCount';
import { DESCRIPTION_MAX, WORKFLOW_NAME_MAX, WORKFLOW_SHORT_DESCRIPTION_MAX } from '../../types/fieldLimits';
import own from './Approvals.module.css';

export interface WorkflowText { name: string; description: string; longDescription: string }

interface Props {
  idPrefix: string;
  value: WorkflowText;
  onChange: (next: WorkflowText) => void;
  disabled?: boolean;
  /** Set once the person has tried to save, so a missing name is said. */
  nameMissing?: boolean;
}

export function WorkflowNameFields({ idPrefix, value, onChange, disabled, nameMissing }: Props) {
  const id = (n: string) => `${idPrefix}-${n}`;
  return (
    <div className={own.textFields}>
      <div className={own.textRow}>
        <div>
          <label className={own.label} htmlFor={id('name')}>Name</label>
          <input
            id={id('name')} maxLength={WORKFLOW_NAME_MAX} className={own.input} value={value.name} aria-required="true" disabled={disabled}
            aria-invalid={Boolean(nameMissing)} aria-describedby={nameMissing ? id('name-error') : undefined}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
          />
          <CharCount value={value.name} max={WORKFLOW_NAME_MAX} />
          {nameMissing && <p className={own.error} id={id('name-error')}>Give it a name people will recognize in the list.</p>}
        </div>
        <div>
          <label className={own.label} htmlFor={id('description')}>Short description <span className={own.optional}>(optional, shown in the list)</span></label>
          <input
            id={id('description')} maxLength={WORKFLOW_SHORT_DESCRIPTION_MAX} className={own.input} value={value.description} disabled={disabled}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
          />
          <CharCount value={value.description} max={WORKFLOW_SHORT_DESCRIPTION_MAX} />
        </div>
      </div>
      <div>
        <label className={own.label} htmlFor={id('long')}>Long description <span className={own.optional}>(optional, shown only on this page)</span></label>
        <textarea
          id={id('long')} maxLength={DESCRIPTION_MAX} className={own.textarea} rows={6} value={value.longDescription} disabled={disabled}
          aria-describedby={id('long-count')} onChange={(e) => onChange({ ...value, longDescription: e.target.value })}
        />
        <CharCount id={id('long-count')} value={value.longDescription} max={DESCRIPTION_MAX} always />
      </div>
    </div>
  );
}
